import React, { useEffect, useMemo } from 'react';
import { Gallery } from '../Gallery';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { PageSection } from '../PageSection';
import { useCardTelemetry, TelemetryProvider, enrichCardTelemetryExtraInfo } from '@apps/card/Card.telemetry';
import { useDesignTemplates, useThemeDisplayOrder, usePriorityThemeId } from './useDesignTemplates';
import { expandTemplatesByDisplayOrder } from './utils';
import { useGalleryItemsWithValueProps } from './useGalleryItemsWithValueProps';
import type { CustomizeNowClickOverrideHandler, StationeryTemplate } from './DesignsGallery.types';
import { useUrlSyncedFilters } from './useUrlSyncedFilters';
import type { GalleryTemplate } from './components/GalleryItem/GalleryItem';
import { GalleryItem } from './components/GalleryItem/GalleryItem';
import type { PriceValuePropAsset } from './propValues.types';
import { useConciergeDisclosure } from './useConciergeDisclosure';
import { BackToTop } from '@shared/components/BackToTop';
import type { FilterFacet } from './components/Filters/Filter.types';
import { DesignGalleryFiltersProvider, useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';
import { useApplySearchFiltersToTemplates } from './useApplySearchFiltersToTemplates';
import { SearchAndFiltersContainer } from './components/Filters/SearchAndFilterContainer';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useStationeryPagination } from './useStationeryPagination';
import { GalleryPagination } from './components/GalleryPagination';
import { GallerySeo } from './DesignsGallerySeo';
import { CardFormat } from '../ProductDetailsPage/constants';
import { useFreeShippingBanner } from '@apps/card/hooks/useFreeShippingBanner';
import { ItemImpressionsTelemetryProvider } from './components/ItemImpressionsTelemetry';
import { FaqSection } from '@apps/card/routes/PublicShop/components/FaqSection';
import { useStationeryFaqItems } from '@apps/card/routes/PublicShop/components/FaqSection/useStationeryFaqItems';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { SeoContentSection } from '@apps/card/routes/PublicShop/components/SeoContentSection';
import { RelatedLinks } from '@apps/card/routes/PublicShop/components/RelatedLinks';
import { SeoReviewsSection } from '@apps/card/routes/PublicShop/components/SeoReviewsSection';
import { BreadcrumbSection } from './components/BreadcrumbSection';
import { useTagFilteredTemplates } from './useTagFilteredTemplates';
import { useFilterFacets } from './useFilterFacets';
import { useProducts } from './useProducts';
import { DesignsGallerySkeleton } from './DesignsGallerySkeleton';
import { useFuse } from './components/Search/useFuse';
import { NoResultsFound } from './NoResultsFound';
import { useSearchFilteredGalleryItems } from './components/Search/useSearchFilteredGalleryItems';
import { GalleryColorwayOrderProvider } from './GalleryColorwayOrderContext';
import { GallerySearchScoresProvider } from './GallerySearchScoresContext';
import type { ColorwayColorHit } from './useColorwayColorHits';
import { useColorwayColorHits } from './useColorwayColorHits';
import { StyleShowcase } from './components/StyleShowcase';
import { useCardPreviewWithPhoto } from '@apps/card/hooks/useCardPreviewWithPhoto';
import { CardPreviewWithPhotoProvider } from '@apps/card/contexts/CardPreviewWithPhotoContext';
import { CustomPhotoPreviewButton } from '@shared/components/CustomPhotoPreviewButton/CustomPhotoPreviewButton';
import { PhotoUploader } from '@apps/admin/components/PhotoUploader/PhotoUploader.lazy';
import { PICKER_TYPES } from '@apps/admin/components/PhotoPickerDialog/PhotoPickerDialog.types';
import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags/featureFlags.provider';
import { useFeatureValue } from '@shared/core/featureFlags';
import { SaveTheDateMarcomFooter } from './SaveTheDateMarcomFooter.lazy';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';

const isGalleryPageSizePayload = (payload: unknown): payload is number => {
  return typeof payload === 'number' && payload > 0;
};

interface DesignsGalleryProps {
  eventId?: string;
  eventHandle?: string;
  // Intended only to be used for the investor demo.
  onCustomizeCardClickOverride?: CustomizeNowClickOverrideHandler;
}

const Root = ({
  filterFacets,
  eventId, // Only used for FavoriteBadge
  eventHandle,
  onCustomizeCardClickOverride,
  templates,
  colorwayColorHits,
}: {
  filterFacets: FilterFacet[];
  templates: ReadonlyArray<StationeryTemplate>;
  colorwayColorHits: ReadonlyArray<ColorwayColorHit>;
} & DesignsGalleryProps) => {
  useUrlSyncedFilters();
  useCardTelemetry({ page: 'homeRoute' });
  const { currentCategory } = useCardCategoryContext();
  enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: currentCategory });
  useEffect(() => {
    enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: null });
  }, []);
  const { useSearchResults, searchError } = useApplySearchFiltersToTemplates();
  const { searchCompleted } = useSearchResults();
  if (searchCompleted && templates.length === 0) {
    searchError();
  }

  const { templates: tagFilteredTemplates, colorwayOrder: filterColorwayOrder, isColorFilterActive } = useTagFilteredTemplates(templates, colorwayColorHits);
  const fuse = useFuse(templates);
  const tagFilteredItems: ReadonlyArray<GalleryTemplate> = useMemo(
    () =>
      tagFilteredTemplates.map(template => ({
        ...template,
        type: 'template',
      })),
    [tagFilteredTemplates],
  );
  const { isActive: isSearchActive, filteredItems, colorwayOrder: searchColorwayOrder, scoresByItem: searchScoresByItem } = useSearchFilteredGalleryItems(tagFilteredItems, fuse);

  // Search already scores every colorway, so when a term is active its ranking
  // supersedes the filter's (coarser) color ordering rather than competing with it.
  const colorwayOrder = useMemo(
    () => (isSearchActive ? { order: searchColorwayOrder, source: 'search' as const } : { order: filterColorwayOrder, source: 'colorFilter' as const }),
    [isSearchActive, searchColorwayOrder, filterColorwayOrder],
  );

  // With nothing narrowing the gallery, expand the list per the gallery-order
  // feature flag: a theme listed multiple times renders as multiple tiles, each
  // pre-selecting the flag's `+colorway`. Both a search term and a color filter
  // ignore the flag — they each pick the colorway a tile should show, which would
  // collapse the flag's deliberately-different repeats into identical tiles.
  const themeOrder = useThemeDisplayOrder();
  const priorityThemeId = usePriorityThemeId(eventHandle);
  const isColorwayNarrowed = isSearchActive || isColorFilterActive;
  const galleryItems = useMemo(
    () => (!isColorwayNarrowed && themeOrder?.length ? expandTemplatesByDisplayOrder(filteredItems as ReadonlyArray<GalleryTemplate>, themeOrder, priorityThemeId) : filteredItems),
    [isColorwayNarrowed, themeOrder, filteredItems, priorityThemeId],
  );

  const { payload: galleryPageSizePayload } = useFeatureValue('printGalleryPageSize');
  const ffPageSize = isGalleryPageSizePayload(galleryPageSizePayload) ? galleryPageSizePayload : undefined;

  const { itemsWithProps, galleryColumnCount } = useGalleryItemsWithValueProps(galleryItems, isSearchActive);
  const { items: paginatedTemplates, ...pagination } = useStationeryPagination(itemsWithProps, galleryColumnCount, isSearchActive, ffPageSize);

  const firstTemplate = useMemo(() => galleryItems.find((item): item is GalleryTemplate => item.type === 'template'), [galleryItems]);

  const { ConciergeDialog, onCardSelect } = useConciergeDisclosure(onCustomizeCardClickOverride);

  const params = useQueryParamHelper();
  const preferredFormat = (params.getValue('format') as CardFormat | undefined) ?? CardFormat.paper;

  const { tq } = useTranslation('stationery');

  const [previewWithPhotoEnabled] = useIsFeatureEnabled('printPreviewWithPhotoCards');
  const { value: galleryTilePricingDisplay } = useFeatureValue('printGalleryTilePricingDisplay');
  const hideTilePrice = galleryTilePricingDisplay === 'treatment';

  const { getTotalActiveFilterCount } = useDesignGalleryFiltersContext();

  const paginatedItems = useMemo(() => {
    if (!hideTilePrice || !firstTemplate || paginatedTemplates.length === 0 || isSearchActive || pagination.page > 1 || getTotalActiveFilterCount() > 0) return paginatedTemplates;
    const priceTile: PriceValuePropAsset & { type: 'priceValueProp' } = {
      type: 'priceValueProp',
      representativeTemplate: firstTemplate,
    };
    const insertAt = Math.min(galleryColumnCount, paginatedTemplates.length);
    return [...paginatedTemplates.slice(0, insertAt), priceTile, ...paginatedTemplates.slice(insertAt)];
  }, [hideTilePrice, firstTemplate, paginatedTemplates, isSearchActive, pagination.page, getTotalActiveFilterCount, galleryColumnCount]);

  const {
    uploadedPreviewImages,
    selectedItemIds,
    handleCustomPhotoUploadCTAClick,
    handlePhotoSelect,
    handleClearAllPhotos,
    isOpenPickerDialog,
    onOpenPickerDialog,
    onClosePickerDialog,
    media,
    isLoadingEventMedia,
  } = useCardPreviewWithPhoto({ enabled: previewWithPhotoEnabled });

  const previewSecondaryFooterAction = useMemo(
    () => (uploadedPreviewImages.length > 0 ? { label: uploadedPreviewImages.length > 1 ? tq`Clear Photos` : tq`Clear Photo`, onClick: handleClearAllPhotos } : undefined),
    [uploadedPreviewImages.length, handleClearAllPhotos, tq],
  );

  return (
    <CardPreviewWithPhotoProvider
      photos={previewWithPhotoEnabled ? uploadedPreviewImages : []}
      isEnabled={previewWithPhotoEnabled}
    >
      {ConciergeDialog}
      <PageSection
        id="designs-gallery"
        paddingX={0}
        titleMarginX={{ _: 6, md: 8 }}
      >
        <SearchAndFiltersContainer
          filterFacets={filterFacets}
          trailingAction={
            eventHandle && previewWithPhotoEnabled ? (
              <CustomPhotoPreviewButton
                size="sm"
                uploadedPreviewImages={uploadedPreviewImages}
                handleCustomPhotoUploadCTAClick={handleCustomPhotoUploadCTAClick}
              />
            ) : undefined
          }
        />
        {(() => {
          if (isSearchActive && !filteredItems.length) return <NoResultsFound />;

          return (
            <GalleryColorwayOrderProvider value={colorwayOrder}>
              <GallerySearchScoresProvider value={searchScoresByItem}>
                <Gallery>
                  {paginatedItems.map((data, index) => {
                    return (
                      <GalleryItem
                        preferredFormat={preferredFormat}
                        cardTileSource="shop"
                        eventId={eventId}
                        onCardSelect={onCardSelect}
                        key={data.type === 'template' ? (data.instanceKey ?? data.id) : index}
                        data={data}
                        currentCategory={currentCategory}
                        hideTilePrice={hideTilePrice}
                      />
                    );
                  })}
                </Gallery>
                {pagination.pages > 1 && (
                  <GalleryPagination
                    currentPage={pagination.page}
                    pageCount={pagination.pages}
                    onPageChange={pagination.setPage}
                  />
                )}
              </GallerySearchScoresProvider>
            </GalleryColorwayOrderProvider>
          );
        })()}
      </PageSection>
      {previewWithPhotoEnabled && (
        <PhotoUploader
          isOpenPickerDialog={isOpenPickerDialog}
          onOpenPickerDialog={onOpenPickerDialog}
          onClosePickerDialog={onClosePickerDialog}
          containerId={eventId || ''}
          eventId={eventId || ''}
          isFetching={isLoadingEventMedia}
          media={media}
          showUploaderEntryPointCTA={false}
          showCount={false}
          pickerType={PICKER_TYPES.PICK_FROM_ALL_PHOTOS}
          onPhotoSelect={handlePhotoSelect}
          maxCount={3}
          minCount={1}
          useCase={{ area: 'admin', subArea: 'stationery' }}
          selectedItems={selectedItemIds}
          secondaryFooterAction={previewSecondaryFooterAction}
        />
      )}
    </CardPreviewWithPhotoProvider>
  );
};

export const DesignsGallery = ({ eventId, eventHandle, onCustomizeCardClickOverride }: DesignsGalleryProps) => {
  const { currentCategory } = useCardCategoryContext();
  const templates = useDesignTemplates({ eventId, eventHandle });
  // Computed once here and threaded down, so the color facet's counts and the
  // filtering they describe are derived from the same colorway matches.
  const colorwayColorHits = useColorwayColorHits(templates);
  const filterFacets = useFilterFacets(templates, colorwayColorHits);

  const { isPublicShopRoute } = useCardsRouterContext();
  const products = useProducts();
  useFreeShippingBanner(products);
  const [styleShowcaseEnabled] = useIsFeatureEnabled('printStyleShowcaseEnabled');
  const faqItems = useStationeryFaqItems(currentCategory);

  return (
    <>
      <GallerySeo
        currentCategory={currentCategory}
        faqItems={faqItems}
        isPublicShopRoute={isPublicShopRoute}
      />
      {isPublicShopRoute && <BreadcrumbSection category={currentCategory} />}
      <SkeletonGroup
        isReady={!!templates.length}
        placeholder={<DesignsGallerySkeleton />}
      >
        <TelemetryProvider>
          <ItemImpressionsTelemetryProvider>
            <DesignGalleryFiltersProvider>
              {styleShowcaseEnabled && <StyleShowcase category={currentCategory} />}
              <Root
                templates={templates}
                colorwayColorHits={colorwayColorHits}
                eventId={eventId}
                eventHandle={eventHandle}
                onCustomizeCardClickOverride={onCustomizeCardClickOverride}
                filterFacets={filterFacets}
              />
              <BackToTop />
            </DesignGalleryFiltersProvider>
          </ItemImpressionsTelemetryProvider>
        </TelemetryProvider>
      </SkeletonGroup>
      {isPublicShopRoute && <RelatedLinks category={currentCategory} />}
      <FaqSection
        faqItems={faqItems}
        category={currentCategory}
      />
      <SeoReviewsSection category={currentCategory} />
      <SeoContentSection category={currentCategory} />
      {isPublicShopRoute && currentCategory === StationeryTemplateCategoryEnum.saveTheDate && <SaveTheDateMarcomFooter />}
    </>
  );
};
